import AppLayout from '@/app-layout.vue';
import PageWrapper from '@/page-wrapper.vue';
import {
  DefaultLayout,
  LegalDocumentView,
  PrivacyOrganizationSettings,
  PrivacyLegalDocuments,
  PrivacyRetentionPolicy,
  AuditJournal,
} from '@fondationcss/ui';
import { WorkflowListView, WorkflowEditView, WorkflowExecutionsView } from '@fondationcss/workflow';
import type { RouteLocationNormalized } from 'vue-router';
import { translate } from '@fondationcss/sdk';
import { GROUP_TRAINING_FEATURE } from '@/utils/group-training-feature';
import { DEMONSTRATION_FEATURE } from '@/utils/demonstration-feature';
import { TEACHER_AVAILABILITY_FEATURE } from '@/utils/teacher-availability-feature';
import { GRICS_IMPORTS_ROUTE_NAME } from '@/utils/grics-import-link';
import { SPECIALIST_VIEW_FEATURE } from '@/utils/specialist-feature';

export type ContextDropdown = 'site' | 'program' | 'group' | 'teacher' | 'users' | 'activity';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    hideFooter?: boolean;
    /**
     * Removes Parcours' PageHeader entirely (breadcrumb + context bar).
     * Reserved for the routes that mount a Fondation view: those carry their own header
     * (a PageHero shipped by @fondationcss/ui), above which the PageHeader would make a
     * second title. They therefore have no breadcrumb at all — that is the trade-off, to
     * be lifted when the Fondation hands its header back.
     * A Parcours view never sets this flag: it would lose its trail.
     */
    hidePageHeader?: boolean;
    contextToolbar?: ContextDropdown[];
    /** Shows appearance controls whose preferences are saved independently for this page. */
    teachingGridAppearance?: boolean;
    /**
     * Offers the « Style du tableau » choice (Classique / Grille) in the Affichage panel.
     * Only the Présences sheet has two renderings; the flag is inert elsewhere.
     */
    teachingGridStyles?: boolean;
    readonly?: boolean;
    groupStatusDefault?: 'confirmed' | 'unconfirmed' | 'all';
    defaultReportContext?: 'enseignement' | 'planification' | 'gestion';
  }
}

export const getCustomRoutes = () => [
  {
    path: '/',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          {
            name: 'home',
            path: '',
            component: () => import('@/views/home.vue'),
            // The only component-bearing route that carried no title: both the
            // browser tab and the breadcrumb fallback had nothing to show (#715).
            meta: { get title() { return translate('parcours.routes.home'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'docs',
            path: '/docs/:pathMatch(.*)',
            component: () => import('@/views/docs.vue'),
            meta: { get title() { return translate('parcours.routes.docs'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'confidentialite',
            path: '/confidentialite',
            component: LegalDocumentView,
            props: { type: 'PrivacyPolicy' },
            // `public: true` (see `default-layout.vue`, css-fondation): Google Play/App Store both
            // require a URL reachable with NO session at all (issue #1055) — without this flag the
            // layout shows the login screen to a signed-out visitor instead of the policy itself.
            meta: { get title() { return translate('parcours.routes.confidentialite'); }, hideFooter: false, contextToolbar: [], public: true }
          },
          {
            name: 'avis-de-collecte',
            path: '/avis-de-collecte',
            component: LegalDocumentView,
            props: { type: 'PrivacyNotice' },
            meta: { get title() { return translate('parcours.routes.avisDeCollecte'); }, hideFooter: false, contextToolbar: [], public: true }
          },
        ]
      },
    ]
  },
  {
    path: '/enseignement',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          // Bare root. The `redirect` lived on the parent, where it never fires: this
          // `path: ''` child captures the resolution as a leaf, PageWrapper renders with an
          // empty RouterView, and the screen shows a blank page under an empty breadcrumb.
          // The redirect must therefore live here, at the only level where `''` was free.
          // Verified in the browser afterwards: the previous fix did not work.
          { path: '', redirect: { name: 'enseignement-tableau-de-bord' } },
          {
            name: 'enseignement-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/teaching/dashboard.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementTableauDeBord'); }, hideFooter: false, contextToolbar: ['site', 'program', 'teacher'] }
          },
          {
            name: 'enseignement-charge-annuelle',
            path: 'charge-annuelle',
            component: () => import('@/views/teaching/teacher-annual-load.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementCalendrier'); }, hideFooter: true, contextToolbar: ['site', 'program', 'teacher'] }
          },
          {
            name: 'enseignement-presences',
            path: 'presences',
            component: () => import('@/views/teaching/presences.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementPresences'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'activity'], teachingGridAppearance: true, teachingGridStyles: true }
          },
          {
            name: 'enseignement-examens',
            path: 'examens',
            component: () => import('@/views/teaching/exams.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementExamens'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'] , fillHeight: true}
          },
          // The student's own dashboard (#957) — the default landing of their section. Its path
          // keeps the `mon-` shape of its neighbours so nothing collides with the teacher's
          // `/enseignement/tableau-de-bord`; only the LABELS dropped the possessive.
          {
            name: 'enseignement-mon-tableau-de-bord',
            path: 'mon-tableau-de-bord',
            component: () => import('@/views/teaching/student-dashboard.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementTableauDeBord'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mes-examens',
            path: 'mes-examens',
            component: () => import('@/views/teaching/my-exams.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementExamens'); }, hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'enseignement-mon-horaire',
            path: 'mon-horaire',
            component: () => import('@/views/teaching/my-schedule.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementCalendrier'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mon-stage',
            path: 'mon-stage',
            component: () => import('@/views/teaching/my-work-placement.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementStage'); }, hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'enseignement-mes-rapports',
            path: 'mes-rapports',
            component: () => import('@/views/teaching/my-reports.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementRapports'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mes-justificatifs',
            path: 'mes-justificatifs',
            component: () => import('@/views/teaching/my-justifications.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementJustificatifs'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mon-suivi',
            path: 'mon-suivi',
            component: () => import('@/views/teaching/my-follow-up.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementSuivi'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-assiduite',
            path: 'assiduite',
            component: () => import('@/views/teaching/attendance.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementAssiduite'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group'], programType: 'group', feature: GROUP_TRAINING_FEATURE, teachingGridAppearance: true }
          },
          {
            name: 'enseignement-progression-groupe',
            path: 'progression-groupe',
            component: () => import('@/views/teaching/group-progression.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementProgressionGroupe'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group'], feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'enseignement-progression-groupe-detail',
            path: 'progression-groupe/detail',
            component: () => import('@/views/teaching/group-progression.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementProgressionGroupe'); }, hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'enseignement-assignations',
            path: 'assignations',
            component: () => import('@/views/teaching/assignments.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementAssignations'); }, hideFooter: true, contextToolbar: ['site', 'program', 'teacher'], programType: 'individual', teachingGridAppearance: true }
          },
          {
            name: 'enseignement-assignation-individuelle',
            path: 'assignation-individuelle',
            component: () => import('@/views/teaching/student-assignments.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementAssignations'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'assignations-individuelles/:frequentationId?',
            redirect: (to: RouteLocationNormalized) => ({
              path: '/enseignement/assignation-individuelle',
              hash: to.params.frequentationId ? `#id=${to.params.frequentationId}` : '',
            })
          },
          {
            name: 'enseignement-vue-consolidee',
            path: 'vue-consolidee',
            component: () => import('@/views/teaching/consolidated-view.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementVueConsolidee'); }, hideFooter: true, contextToolbar: ['site', 'program', 'teacher'], programType: 'individual', teachingGridAppearance: true }
          },
          // #1226 — the specialist's read-only follow-up. The flag sits on both routes as on the menu
          // entry: off, a bookmark lands on the teacher dashboard.
          {
            name: 'enseignement-eleves-suivis',
            path: 'eleves-suivis',
            component: () => import('@/views/teaching/followed-students.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementElevesSuivis'); }, hideFooter: true, contextToolbar: [], fillHeight: true, feature: SPECIALIST_VIEW_FEATURE }
          },
          {
            name: 'enseignement-eleves-suivis-detail',
            path: 'eleves-suivis/detail',
            component: () => import('@/views/teaching/followed-student.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementEleveSuivi'); }, hideFooter: true, contextToolbar: [], feature: SPECIALIST_VIEW_FEATURE }
          },
          {
            name: 'enseignement-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { get title() { return translate('parcours.routes.enseignementRapports'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'], defaultReportContext: 'enseignement' }
          },
        ]
      },
    ]
  },
  {
    path: '/planification',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          // Bare root. The `redirect` lived on the parent, where it never fires: this
          // `path: ''` child captures the resolution as a leaf, PageWrapper renders with an
          // empty RouterView, and the screen shows a blank page under an empty breadcrumb.
          // The redirect must therefore live here, at the only level where `''` was free.
          // Verified in the browser afterwards: the previous fix did not work.
          { path: '', redirect: { name: 'planification-tableau-de-bord' } },
          {
            name: 'planification-disponibilites', path: 'disponibilites',
            component: () => import('@/views/planning/teacher-offerings.vue'),
            meta: { get title() { return translate('parcours.routes.planificationDisponibilites'); }, contextToolbar: ['program', 'teacher'], feature: TEACHER_AVAILABILITY_FEATURE }
          },
          {
            name: 'planification-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/planning/dashboard.vue'),
            meta: { get title() { return translate('parcours.routes.planificationTableauDeBord'); }, hideFooter: false, contextToolbar: ['site', 'program', 'group'] }
          },
          {
            name: 'planification-simulation',
            path: 'simulation',
            component: () => import('@/views/planning/simulation.vue'),
            meta: { get title() { return translate('parcours.routes.planificationSolveur'); }, hideFooter: true, contextToolbar: ['site', 'program'], feature: GROUP_TRAINING_FEATURE }
          },
          ...(import.meta.env.DEV ? [{
            name: 'planification-test-solveur-v2',
            path: 'test-solveur-v2',
            component: () => import('@/views/planning/solver-test-v2.vue'),
            meta: { get title() { return translate('parcours.routes.planificationTestSolveurV2'); }, hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          }] : []),
          {
            name: 'planification-etudiants',
            // vocabulary-ignore: a bookmarked URL is a contract; the page itself says « Élèves »
            path: 'etudiants',
            component: () => import('@/views/planning/students.vue'),
            meta: { get title() { return translate('parcours.routes.planificationGestionEleves'); }, hideFooter: true, contextToolbar: ['users'] , fillHeight: true}
          },
          {
            name: 'planification-etudiant-detail',
            path: 'etudiants/detail',
            component: () => import('@/views/planning/student.vue'),
            // No fillHeight: a form of fields, not a grid — it must be able to scroll like
            // any other form (#710, #717).
            meta: { get title() { return translate('parcours.routes.planificationEleve'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'etudiants/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/etudiants/detail', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-frequentations',
            path: 'frequentations',
            component: () => import('@/views/planning/enrollments.vue'),
            meta: { get title() { return translate('parcours.routes.planificationGestionFrequentations'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group', 'users'] , fillHeight: true}
          },
          {
            name: 'planification-frequentation',
            path: 'frequentation',
            component: () => import('@/views/planning/frequentation-form.vue'),
            meta: { get title() { return translate('parcours.routes.planificationFrequentation'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'frequentations/new',
            redirect: () => ({ path: '/planification/frequentation', hash: '#new=1' })
          },
          {
            path: 'frequentations/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/frequentation', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-sequencage',
            path: 'sequencage',
            component: () => import('@/views/planning/group-sequencing.vue'),
            meta: { get title() { return translate('parcours.routes.planificationSequencage'); }, hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'planification-sequencage-edition',
            path: 'sequencage-edition',
            component: () => import('@/views/planning/group-sequencing-editor.vue'),
            meta: { get title() { return translate('parcours.routes.planificationSequenceGroupe'); }, hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          },
          {
            path: 'sequencage/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/sequencage', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-vue-ensemble',
            path: 'vue-ensemble',
            component: () => import('@/views/planning/groups.vue'),
            meta: { get title() { return translate('parcours.routes.planificationGroupes'); }, hideFooter: true, contextToolbar: ['site', 'program'], fillHeight: true, feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'planification-affectation',
            path: 'affectation',
            component: () => import('@/views/planning/group-members.vue'),
            meta: { get title() { return translate('parcours.routes.planificationElevesGroupe'); }, hideFooter: true, contextToolbar: [], fillHeight: true, feature: GROUP_TRAINING_FEATURE }
          },
          {
            path: 'affectation/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/affectation', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-individualise',
            path: 'individualise',
            component: () => import('@/views/planning/individual-plannings.vue'),
            meta: { get title() { return translate('parcours.routes.planificationElevesIndividualises'); }, hideFooter: true, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'planification-individualise-detail',
            path: 'individualise/detail',
            component: () => import('@/views/planning/individual-planning.vue'),
            meta: { get title() { return translate('parcours.routes.planificationEleveIndividualise'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'individualise/:frequentationId',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/individualise/detail', hash: `#id=${to.params.frequentationId}` })
          },
          {
            // Courses given to groups of individualised students (#821).
            name: 'planification-cours-de-groupe',
            path: 'cours-de-groupe',
            component: () => import('@/views/planning/group-courses.vue'),
            meta: { get title() { return translate('parcours.routes.planificationCoursGroupe'); }, hideFooter: true, contextToolbar: ['program'], fillHeight: true }
          },
          {
            // Moved to Gestion › ATE & Stages (#1180); kept for bookmarks. Object redirects carry
            // the query and the fragment, so `#id=` still opens the same placement.
            path: 'stages',
            redirect: { name: 'gestion-stages' }
          },
          {
            path: 'stage',
            redirect: { name: 'gestion-stage' }
          },
          {
            path: 'stages/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/gestion/stage', hash: `#id=${to.params.id}` })
          },
          {
            // « Gestion des absences » (#720) — the day read by who is missing.
            name: 'planification-absences',
            path: 'absences',
            component: () => import('@/views/planning/absences.vue'),
            meta: { get title() { return translate('parcours.routes.planificationAbsences'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            // Moved to Gestion › Externe (#1167); kept for bookmarks. An object redirect carries
            // the query and the fragment, so `#id=` still opens the same enterprise.
            path: 'entreprises',
            redirect: { name: 'gestion-entreprises' }
          },
          {
            name: 'planification-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { get title() { return translate('parcours.routes.planificationRapports'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'], defaultReportContext: 'planification' }
          },
        ]
      },
    ]
  },
  {
    path: '/gestion',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          // Bare root. The `redirect` lived on the parent, where it never fires: this
          // `path: ''` child captures the resolution as a leaf, PageWrapper renders with an
          // empty RouterView, and the screen shows a blank page under an empty breadcrumb.
          // The redirect must therefore live here, at the only level where `''` was free.
          // Verified in the browser afterwards: the previous fix did not work.
          { path: '', redirect: { name: 'gestion-tableau-de-bord' } },
          {
            name: 'gestion-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/management/dashboard.vue'),
            meta: { get title() { return translate('parcours.routes.gestionTableauDeBord'); }, hideFooter: false, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'gestion-enseignants',
            path: 'enseignants',
            component: () => import('@/views/management/teachers.vue'),
            meta: { get title() { return translate('parcours.routes.gestionEnseignants'); }, hideFooter: true, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'gestion-enseignant-detail',
            path: 'enseignants/detail',
            component: () => import('@/views/management/teacher.vue'),
            // No fillHeight: a mixed record (identity/colour, affinities+schedule, load) that
            // scrolls like any other detail page (#710, #717, #719) — like
            // enseignement/charge-annuelle today, which is not fillHeight either despite its
            // own calendar + grid.
            meta: { get title() { return translate('parcours.routes.gestionEnseignant'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'enseignants/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/gestion/enseignants/detail', hash: `#id=${to.params.id}` })
          },
          {
            name: 'gestion-embauches',
            path: 'embauches',
            component: () => import('@/views/management/hires.vue'),
            meta: { get title() { return translate('parcours.routes.gestionEmbauches'); }, hideFooter: true, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'gestion-centres',
            path: 'centres',
            component: () => import('@/views/management/sites.vue'),
            meta: { get title() { return translate('parcours.routes.gestionCentres'); }, hideFooter: false, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'gestion-entreprises',
            path: 'entreprises',
            component: () => import('@/views/management/enterprises.vue'),
            meta: { get title() { return translate('parcours.routes.gestionEntreprises'); }, hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            // The work-study internships, under Gestion › ATE & Stages since #1180.
            name: 'gestion-stages',
            path: 'stages',
            component: () => import('@/views/planning/work-placements.vue'),
            meta: { get title() { return translate('parcours.routes.gestionStages'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group'] , fillHeight: true}
          },
          {
            name: 'gestion-stage',
            path: 'stage',
            component: () => import('@/views/planning/work-placement.vue'),
            meta: { get title() { return translate('parcours.routes.gestionStage'); }, hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'gestion-lieux-examen',
            path: 'lieux-examen',
            component: () => import('@/views/management/exam-locations.vue'),
            meta: { get title() { return translate('parcours.routes.gestionLieuxExamen'); }, hideFooter: false, contextToolbar: [], fillHeight: true }
          },
          {
            name: 'gestion-locaux-ressources',
            path: 'locaux-ressources',
            component: () => import('@/views/management/locals-resources.vue'),
            meta: { get title() { return translate('parcours.routes.gestionLocauxRessources'); }, hideFooter: false, contextToolbar: ['site'], fillHeight: true }
          },
          {
            name: 'gestion-locaux-occupation',
            path: 'locaux-ressources/occupation',
            component: () => import('@/views/management/locals-occupancy.vue'),
            meta: { get title() { return translate('parcours.routes.gestionOccupationLocaux'); }, hideFooter: false, contextToolbar: ['site'] }
          },
          {
            name: 'gestion-programmes',
            path: 'programmes',
            component: () => import('@/views/management/programs.vue'),
            meta: { get title() { return translate('parcours.routes.gestionProgrammes'); }, hideFooter: false, contextToolbar: ['site'] }
          },
          {
            name: 'gestion-programme',
            path: 'programme',
            component: () => import('@/views/management/program.vue'),
            meta: { get title() { return translate('parcours.routes.gestionProgramme'); }, hideFooter: false, contextToolbar: [] }
          },
          {
            name: 'gestion-module',
            path: 'programme/module',
            component: () => import('@/views/management/module.vue'),
            meta: { get title() { return translate('parcours.routes.gestionCompetence'); }, hideFooter: false, contextToolbar: [] }
          },
          {
            path: 'programmes/:programId/modules/:moduleId',
            redirect: (to: RouteLocationNormalized) => ({
              path: '/gestion/programme/module',
              hash: `#programid=${to.params.programId}&id=${to.params.moduleId}`,
            })
          },
          {
            path: 'programmes/:programId',
            redirect: (to: RouteLocationNormalized) => ({ path: '/gestion/programme', hash: `#id=${to.params.programId}` })
          },
          {
            name: 'gestion-gabarits',
            path: 'gabarits',
            component: () => import('@/views/management/templates.vue'),
            meta: { get title() { return translate('parcours.routes.gestionGabarits'); }, hideFooter: false, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'gestion-references',
            path: 'references',
            component: () => import('@/views/management/references.vue'),
            meta: { get title() { return translate('parcours.routes.gestionReferences'); }, hideFooter: false, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'gestion-demonstration',
            path: 'demonstration',
            component: () => import('@/views/management/demo.vue'),
            // The sidebar entry already carries the flag; the route needs it too, or the page
            // still opens by deep link on an instance that turned demonstration off — with every
            // API call behind it answering 404.
            meta: { get title() { return translate('parcours.routes.gestionDemonstration'); }, hideFooter: false, contextToolbar: [] , fillHeight: true, feature: DEMONSTRATION_FEATURE }
          },
          {
            name: 'gestion-confidentialite-parametres',
            path: 'confidentialite/parametres',
            component: PrivacyOrganizationSettings,
            props: {
              conservationRoute: '/gestion/confidentialite/parametres/conservation',
              documentsRoute: '/gestion/confidentialite/parametres/documents',
            },
            meta: { get title() { return translate('parcours.routes.gestionParamConfidentialite'); }, hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            name: 'gestion-confidentialite-documents',
            path: 'confidentialite/parametres/documents',
            component: PrivacyLegalDocuments,
            props: {
              settingsRoute: '/gestion/confidentialite/parametres',
              privacyNoticeRoute: '/avis-de-collecte',
              privacyPolicyRoute: '/confidentialite',
            },
            meta: { get title() { return translate('parcours.routes.gestionDocumentsLegaux'); }, hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            name: 'gestion-confidentialite-conservation',
            path: 'confidentialite/parametres/conservation',
            component: PrivacyRetentionPolicy,
            meta: { get title() { return translate('parcours.routes.gestionConservationSuppression'); }, hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            name: 'gestion-audit',
            path: 'audit',
            component: AuditJournal,
            meta: { get title() { return translate('parcours.routes.gestionJournalAudit'); }, hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            // The four GRICS imports in one place (#989); the pages they write to link here.
            name: GRICS_IMPORTS_ROUTE_NAME,
            path: 'importations-grics',
            component: () => import('@/views/management/grics-imports.vue'),
            meta: { get title() { return translate('parcours.routes.gestionImportationsGRICS'); }, hideFooter: false, contextToolbar: [], fillHeight: true }
          },
          {
            name: 'gestion-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { get title() { return translate('parcours.routes.gestionRapports'); }, hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'], defaultReportContext: 'gestion' }
          },
        ]
      },
    ]
  },
  {
    path: '/workflow',
    component: DefaultLayout,
    children: [
      {
        path: '',
        children: [
          {
            name: 'workflow-list',
            path: '',
            component: WorkflowListView,
            meta: { get title() { return translate('parcours.routes.workflowFluxTravail'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'workflow-edit',
            path: ':id',
            component: WorkflowEditView,
            meta: { get title() { return translate('parcours.routes.workflowFluxTravail'); }, hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'workflow-executions',
            path: ':id/executions',
            component: WorkflowExecutionsView,
            meta: { get title() { return translate('parcours.routes.workflowHistorique'); }, hideFooter: true, contextToolbar: [] }
          },
        ]
      },
    ]
  },
];
